import { CLIENT_REFRESH_THRESHOLD_MS } from '@/convex/media/url_freshness';
import { createIdentityScopedSessionMemory } from '@/lib/identity-scoped-session-memory';

/**
 * Client memory of URLs the storage resolver authorized, bound to the actor
 * that was authorized.
 *
 * A signed URL is a bearer token: whoever holds it can fetch the object until
 * it expires, with no further access check. So this memory is only ever a
 * placeholder while the live, access-checked `resolveStorageUrl` subscription
 * loads, and it is:
 * - scoped to the confirmed identity AND the active workspace (an entry is
 *   never served to another account, another workspace, or with no identity);
 * - wiped synchronously on every confirmed-identity change (sign-out, account
 *   switch) by the Convex auth lifecycle, and on workspace access loss;
 * - forgotten as soon as the live resolver denies the key;
 * - for private objects, served only within `PRIVATE_PLACEHOLDER_MAX_AGE_MS`
 *   of the last access check, well below the signed-URL TTL.
 */
export type CachedEntry = {
  url: string | null;
  urlExpiresAt: number | null;
  /** When an authorized resolver/refresh last vouched for this URL. */
  validatedAt: number;
};

/**
 * How long a private URL may stand in for a live access check. Signed URLs
 * live 180–300 s; a member removed from a thread must not be able to remount a
 * viewer and get one back from memory for the rest of that window.
 */
export const PRIVATE_PLACEHOLDER_MAX_AGE_MS = 30_000;

// Bounded so a long session over a large library cannot grow without limit.
const STORAGE_URL_MEMORY_LIMIT = 2_000;

const storageUrlMemory = createIdentityScopedSessionMemory<CachedEntry>(
  STORAGE_URL_MEMORY_LIMIT
);

// Legacy Convex `_storage` ids (no '/') resolve through the historical
// `assets.queries.getStorageUrl` path. Null results are remembered too, so a
// missing id isn't re-queried by every mount.
const legacyStorageIdMemory = createIdentityScopedSessionMemory<{
  url: string | null;
}>(STORAGE_URL_MEMORY_LIMIT);

/**
 * The actor a cached URL belongs to: confirmed identity plus the active
 * workspace. Null (no confirmed identity) disables the memory entirely.
 */
export function storageUrlCacheScope(
  identity: string | null,
  orgSlug: string | null
): string | null {
  if (!identity) return null;
  return `${identity}\u0000${orgSlug ?? ''}`;
}

/** Usable as a refresh source: has a URL that isn't near expiry. */
export function cachedEntryIsFresh(
  entry: CachedEntry | null | undefined,
  now: number = Date.now()
): entry is CachedEntry {
  if (!entry || entry.url == null) return false;
  if (entry.urlExpiresAt == null) return true; // public URL, no expiry
  return entry.urlExpiresAt - now > CLIENT_REFRESH_THRESHOLD_MS;
}

/**
 * Usable WITHOUT a live access decision: fresh, and for private (expiring)
 * URLs, access-checked within the placeholder window.
 */
export function cachedEntryIsServable(
  entry: CachedEntry | null | undefined,
  now: number = Date.now()
): entry is CachedEntry {
  if (!cachedEntryIsFresh(entry, now)) return false;
  if (entry.urlExpiresAt == null) return true;
  return now - entry.validatedAt <= PRIVATE_PLACEHOLDER_MAX_AGE_MS;
}

export function readStorageUrl(
  scope: string | null,
  storageKey: string
): CachedEntry | null {
  return storageUrlMemory.get(scope, storageKey);
}

export function writeStorageUrl(
  scope: string | null,
  storageKey: string,
  entry: CachedEntry
): void {
  storageUrlMemory.set(scope, storageKey, entry);
}

/**
 * Moves on every clear (sign-out, account switch, workspace access loss).
 * Async refreshes capture it before awaiting and discard their result if it
 * moved, so a completion can never repopulate a memory that was wiped.
 */
export function storageUrlCacheGeneration(): number {
  return storageUrlMemory.generation();
}

/** Drop one key, e.g. after the resolver denied it. */
export function forgetStorageUrl(storageKey: string): void {
  storageUrlMemory.forget(storageKey);
}

export function readLegacyStorageUrl(
  scope: string | null,
  cacheKey: string
): { url: string | null } | null {
  return legacyStorageIdMemory.get(scope, cacheKey);
}

export function writeLegacyStorageUrl(
  scope: string | null,
  cacheKey: string,
  url: string | null
): void {
  legacyStorageIdMemory.set(scope, cacheKey, { url });
}

/**
 * Forget every remembered storage URL. Call on any client-observed access
 * change (membership loss, share revoke); mounted consumers keep rendering
 * only what their live resolver subscription currently authorizes.
 */
export function invalidateStorageUrlCache(): void {
  storageUrlMemory.clear();
  legacyStorageIdMemory.clear();
}

/**
 * Picks the URL to render for a member storage key, or null.
 *
 * With a live resolver result, its decision wins: a denial returns null even
 * if memory still holds a valid-looking URL. Otherwise the newest of the live
 * URL, this component's own refresh, and the actor's memory is used. Without a
 * live result (still loading), only entries servable without a fresh access
 * check may stand in.
 */
export function pickMemberStorageUrl({
  live,
  refreshed,
  cached,
}: {
  live:
    | {
        url: string | null;
        urlExpiresAt: number | null;
        refreshRequired: boolean;
      }
    | undefined;
  refreshed: CachedEntry | null;
  cached: CachedEntry | null;
}): string | null {
  if (live && live.url === null && !live.refreshRequired) return null;
  const now = Date.now();
  const usable = live ? cachedEntryIsFresh : cachedEntryIsServable;
  const candidates: Array<Pick<CachedEntry, 'url' | 'urlExpiresAt'>> = [];
  if (usable(refreshed, now)) candidates.push(refreshed);
  if (usable(cached, now)) candidates.push(cached);
  if (live) candidates.push(live);
  let best: { url: string; expiresAt: number } | null = null;
  for (const { url, urlExpiresAt } of candidates) {
    if (!url) continue;
    // A public URL never expires, so it outranks any signed one.
    const expiresAt = urlExpiresAt ?? Number.POSITIVE_INFINITY;
    if (!best || expiresAt > best.expiresAt) best = { url, expiresAt };
  }
  return best?.url ?? null;
}
